import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchAttendanceReport } from "../redux/actions/attendanceActions";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import theme from "../components/shared/theme";
import {
  tdStyle,
  thStyle,
  paginationButtonStyle,
  dropdownStyle,
} from "../components/shared/themeStyles";
import ExcelJS from "exceljs";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AttendanceReport = () => {
  const dispatch = useDispatch();

  const { attendanceReport = [], loading, error, reportPagination } =
    useSelector((state) => state.attendance);

  const { districts = [], municipalities = [] } = useSelector(
    (state) => state.location || {}
  );
  const { designations = [] } = useSelector((state) => state.designation || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showFilters, setShowFilters] = useState(true);

  const itemsPerPage = 10;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(
      fetchAttendanceReport({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
      })
    );
  }, [
    dispatch, currentPage, selectedDistrict, selectedMunicipality,
    fromDate, toDate, searchTerm, selectedDesignation,
  ]);

  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  const selectedDistrictObj = districts.find((d) => d.name === selectedDistrict);
  const selectedDistrictId = selectedDistrictObj?.id;
  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter((m) => m.district_id === selectedDistrictId)
    : municipalities;

  useEffect(() => { setSelectedMunicipality(""); }, [selectedDistrict]);

  const leaveTypes = Array.from(
    new Set(
      attendanceReport.flatMap((att) =>
        att.leave_summary ? Object.keys(att.leave_summary) : []
      )
    )
  );

  const formatLeaveName = (leave) => {
    const shortNames = {
      'casual leave': 'CL',
      'earned leave': 'EL',
      'maternity leave': 'ML',
      'sick leave': 'SL',
      'special leave': 'SPL',
    };
    return shortNames[leave.toLowerCase()] || leave.substring(0, 3).toUpperCase();
  };

  const hexToArgb = (hex) => 'FF' + hex.replace('#', '').toUpperCase();
  const borderStyle = { style: 'thin', color: { argb: 'FF999999' } };

  // ---------- Excel Export (unchanged) ----------
  const handleDownloadExcel = async () => {
    if (downloadingExcel) return;
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getAttendanceReport?${queryParams}`,
        { headers: { authorization: token } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const reportData = data.data || [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Attendance Report", {
        views: [{ state: 'frozen', ySplit: 2 }],
      });

      const sortedLeaves = leaveTypes.sort();
      const totalCols = 14 + sortedLeaves.length * 3;

      worksheet.mergeCells(1, 1, 1, totalCols);
      const titleCell = worksheet.getCell(1, 1);
      titleCell.value = `Attendance Report - ${fromDate || 'Start'} to ${toDate || 'End'}`;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
      worksheet.getRow(1).height = 25;

      const headerRow1 = ['District', 'ULB', 'Employee', 'Phone', 'Designation',
        'WD', 'P', 'A', 'H', 'S', 'Lv', 'Late', 'Early', 'CL Ded'];
      const headerRow2 = ['', '', '', '', '', '', '', '', '', '', '', '', '', ''];
      sortedLeaves.forEach(leave => {
        headerRow1.push(formatLeaveName(leave), '', '');
        headerRow2.push('Elg', 'Avl', 'Rmn');
      });

      worksheet.addRow(headerRow1);
      worksheet.addRow(headerRow2);

      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      for (let col = 1; col <= totalCols; col++) {
        const c1 = worksheet.getCell(2, col);
        const c2 = worksheet.getCell(3, col);
        c1.fill = headerFill; c1.font = { bold: true, size: 9 };
        c1.alignment = { horizontal: 'center', vertical: 'middle' };
        c1.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
        c2.fill = headerFill; c2.font = { bold: true, size: 8 };
        c2.alignment = { horizontal: 'center', vertical: 'middle' };
        c2.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
      }

      let currentRow = 4;
      reportData.forEach(emp => {
        const s = emp.attendance_summary || {};
        const rowVals = [
          emp.district, emp.municipality, emp.employee_name, emp.phone, emp.designation,
          s.working_days || 0, s.present || 0, s.absent || 0, s.holiday || 0, s.sunday || 0,
          s.approved_leaves || 0, s.late || 0, s.early_departure || 0, s.cl_deduction || 0,
        ];
        sortedLeaves.forEach(leave => {
          const l = emp.leave_summary?.[leave] || {};
          rowVals.push(l.eligible || 0, l.availed || 0, l.remaining || 0);
        });
        const row = worksheet.addRow(rowVals);
        for (let col = 1; col <= rowVals.length; col++) {
          const cell = row.getCell(col);
          cell.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          if (col === 7 && s.present > 0) cell.font = { color: { argb: 'FF2E7D32' }, bold: true };
          if (col === 8 && s.absent > 0) cell.font = { color: { argb: 'FFC62828' }, bold: true };
          if (col === 12 && s.late > 0) cell.font = { color: { argb: 'FFED6C02' }, bold: true };
          if (col === 13 && s.early_departure > 0) cell.font = { color: { argb: 'FF9C27B0' }, bold: true };
          if (col === 14 && s.cl_deduction > 0) cell.font = { color: { argb: 'FFD32F2F' }, bold: true };
        }
        currentRow++;
      });

      worksheet.columns.forEach((col, idx) => {
        if (idx < 5) col.width = 15;
        else col.width = 8;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Attendance_Report_${fromDate || 'all'}_to_${toDate || 'all'}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel download error:", err);
      alert("Failed to download Excel: " + err.message);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const badgeStyle = (type) => ({
    backgroundColor: type === 'primary' ? '#e3f2fd' :
                    type === 'success' ? '#e8f5e9' :
                    type === 'danger' ? '#ffebee' :
                    type === 'warning' ? '#fff3e0' :
                    type === 'info' ? '#e1f5fe' :
                    type === 'orange' ? '#fff3e0' :
                    type === 'purple' ? '#f3e5f5' :
                    type === 'red' ? '#ffebee' : '#f5f5f5',
    color: type === 'primary' ? '#1976d2' :
           type === 'success' ? '#2e7d32' :
           type === 'danger' ? '#c62828' :
           type === 'warning' ? '#ed6c02' :
           type === 'info' ? '#0288d1' :
           type === 'orange' ? '#ed6c02' :
           type === 'purple' ? '#9c27b0' :
           type === 'red' ? '#c62828' : '#616161',
    padding: "4px 8px", borderRadius: "16px", fontSize: "0.7rem", fontWeight: 500,
  });

  const formatCountBadge = (value, type) => {
    if (!value || value === 0) return <span style={{ color: "#10b981", backgroundColor: "#e6f7e6", padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem" }}>0</span>;
    const days = parseInt(value);
    let color, bg;
    if (type === 'late') { color = days>5 ? "#ef4444" : "#f59e0b"; bg = days>5 ? "#fee2e2" : "#fff3e0"; }
    else if (type === 'early') { color = "#9c27b0"; bg = "#f3e5f5"; }
    else if (type === 'absent') { color = "#c62828"; bg = "#ffebee"; }
    return <span style={{ color, fontWeight: 600, backgroundColor: bg, padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem" }}>{days}</span>;
  };

  const totalColumns = 14 + leaveTypes.length * 3;
  const isMobile = windowWidth < 768;

  // ----- UPDATED: Responsive table styling -----
  const tableFontSize = isMobile ? "0.65rem" : "0.7rem";
  const tableMinWidth = isMobile ? "900px" : "100%";      // 100% on desktop – container will scroll if needed

  return (
    <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.lg, backgroundColor: theme.colors.background, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing.lg, flexWrap: "wrap", gap: theme.spacing.sm }}>
        <h1 style={{ fontSize: isMobile ? theme.font.size.xl : theme.font.size["2xl"], fontWeight: theme.font.weight.bold, color: theme.colors.grayDark, margin: 0 }}>
          Attendance Report
        </h1>
        {isMobile && (
          <button onClick={() => setShowFilters(!showFilters)} style={{ backgroundColor: theme.colors.primary, color: "#fff", border: "none", borderRadius: theme.radius.md, padding: `${theme.spacing.xs} ${theme.spacing.md}`, fontSize: "0.8rem" }}>
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: isMobile && !showFilters ? "none" : "flex", flexDirection: isMobile ? "column" : "row", flexWrap: "wrap", gap: theme.spacing.sm, marginBottom: theme.spacing.lg, backgroundColor: "#fff", padding: isMobile ? theme.spacing.sm : theme.spacing.md, borderRadius: theme.radius.xl, boxShadow: theme.shadow.soft }}>
        <select value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} style={{...dropdownStyle, width: isMobile ? "100%" : "auto", fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel}>
          <option value="">All Districts</option>
          {districts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <select value={selectedMunicipality} onChange={e => setSelectedMunicipality(e.target.value)} style={{...dropdownStyle, width: isMobile ? "100%" : "auto", fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel}>
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
        <select value={selectedDesignation} onChange={e => setSelectedDesignation(e.target.value)} style={{...dropdownStyle, width: isMobile ? "100%" : "auto", fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel}>
          <option value="">All Designations</option>
          {designations.map((d, idx) => <option key={idx} value={d}>{d}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{...dropdownStyle, minWidth: isMobile ? "100%" : "150px", fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel} />
        <div style={{ display: "flex", gap: theme.spacing.sm, width: isMobile ? "100%" : "auto" }}>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{...dropdownStyle, flex:1, fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{...dropdownStyle, flex:1, fontSize: isMobile ? "0.8rem":"0.85rem"}} disabled={downloadingExcel} />
        </div>
        <div style={{ display: "flex", gap: theme.spacing.sm, width: isMobile ? "100%" : "auto" }}>
          <button onClick={() => { setFromDate(""); setToDate(""); setSelectedDistrict(""); setSelectedMunicipality(""); setSelectedDesignation(""); setSearchTerm(""); setCurrentPage(1); }} disabled={downloadingExcel} style={{ backgroundColor: downloadingExcel ? theme.colors.gray : theme.colors.primary, color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: downloadingExcel ? "not-allowed" : "pointer", fontWeight: "bold", flex: isMobile ? 1 : "none", minWidth: isMobile ? "auto" : "80px", opacity: downloadingExcel ? 0.7 : 1, fontSize: isMobile ? "0.8rem":"0.85rem" }}>
            Reset
          </button>
          <button onClick={handleDownloadExcel} disabled={downloadingExcel} style={{ backgroundColor: downloadingExcel ? theme.colors.gray : '#28a745', color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: downloadingExcel ? "not-allowed" : "pointer", fontWeight: "bold", flex: isMobile ? 1 : "none", minWidth: "100px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center", gap: theme.spacing.sm, opacity: downloadingExcel ? 0.8 : 1, fontSize: isMobile ? "0.8rem":"0.85rem" }}>
            {downloadingExcel ? <><div className="spinner-small" /><span>Excel...</span></> : <><span>📊</span><span>Excel</span></>}
          </button>
        </div>
      </div>

      {/* Downloading overlay */}
      {downloadingExcel && (
        <div style={{ position: "fixed", top:0, left:0, right:0, bottom:0, backgroundColor:"rgba(255,255,255,0.85)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
          <div className="spinner" />
          <p style={{ marginTop:theme.spacing.md, fontSize:theme.font.size.md, fontWeight:"bold", color:theme.colors.grayDark }}>Generating Excel... Please wait</p>
        </div>
      )}

      {/* Main content */}
      {isMobile ? (
        // Mobile cards (unchanged)
        <div>
          {loading && <div style={{ textAlign:"center", padding:"40px" }}><div className="spinner" /></div>}
          {error && <div style={{ textAlign:"center", padding:"40px", color:"red" }}>{error}</div>}
          {!loading && !error && attendanceReport.length === 0 && <div style={{ textAlign:"center", padding:"40px" }}>No records found</div>}
          {!loading && !error && attendanceReport.map((att, idx) => (
            <div key={att.user_id} style={{ backgroundColor:"#fff", borderRadius:theme.radius.lg, padding:theme.spacing.md, marginBottom:theme.spacing.sm, boxShadow:theme.shadow.soft, border:"1px solid #eee" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:theme.spacing.sm }}>
                <span style={{ fontWeight:"bold", color:theme.colors.primary }}>{att.employee_name}</span>
                <span style={{ color:theme.colors.gray, fontSize:"0.7rem" }}>#{(reportPagination?.currentPage-1)*itemsPerPage + idx + 1}</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:theme.spacing.xs, fontSize:"0.75rem" }}>
                <div><strong>District:</strong> {att.district}</div>
                <div><strong>ULB:</strong> {att.municipality}</div>
                <div><strong>Phone:</strong> {att.phone}</div>
                <div><strong>Designation:</strong> {att.designation}</div>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:theme.spacing.xs, marginTop:theme.spacing.sm, padding:theme.spacing.xs, backgroundColor:theme.colors.grayLight, borderRadius:theme.radius.md }}>
                <div style={badgeStyle("primary")}>WD: {att.attendance_summary?.working_days||0}</div>
                <div style={badgeStyle("success")}>P: {att.attendance_summary?.present||0}</div>
                <div style={badgeStyle("danger")}>A: {att.attendance_summary?.absent||0}</div>
                <div style={badgeStyle("warning")}>H: {att.attendance_summary?.holiday||0}</div>
                <div style={badgeStyle("secondary")}>S: {att.attendance_summary?.sunday||0}</div>
                <div style={badgeStyle("info")}>Lv: {att.attendance_summary?.approved_leaves||0}</div>
                <div style={badgeStyle("orange")}>Late: {att.attendance_summary?.late||0}</div>
                <div style={badgeStyle("purple")}>Early: {att.attendance_summary?.early_departure||0}</div>
                <div style={badgeStyle("red")}>CL Ded: {att.attendance_summary?.cl_deduction||0}</div>
              </div>
              <div style={{ marginTop:theme.spacing.sm }}>
                <div style={{ fontSize:"0.7rem", fontWeight:"bold", marginBottom:theme.spacing.xs }}>Leave Summary:</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:theme.spacing.xs }}>
                  {Object.entries(att.leave_summary||{}).map(([leave, data]) => (
                    <div key={leave} style={{ backgroundColor:'#f3e5f5', color:'#7b1fa2', padding:"2px 8px", borderRadius:"12px", fontSize:"0.7rem" }}>
                      <strong>{formatLeaveName(leave)}:</strong> {data.eligible}/{data.availed}/{data.remaining}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop table – RESPONSIVE
        <div style={{
          backgroundColor: "#fff",
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
          overflowX: "auto",
          position: "relative",
          width: "100%",               // take full width of parent
          maxWidth: "100%",            // never exceed viewport
          margin: "0 auto",
        }}>
          {loading && (
            <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, backgroundColor:"rgba(255,255,255,0.8)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10 }}>
              <div className="spinner" />
            </div>
          )}
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: tableFontSize,
            minWidth: tableMinWidth,
            tableLayout: "auto",
          }}>
            <thead style={{ backgroundColor:theme.colors.grayDark, color:"#fff", position:"sticky", top:0, zIndex:1 }}>
              <tr>
                <th style={{...thStyle, width: "2%", padding: "4px 2px"}} rowSpan="2">#</th>
                <th style={{...thStyle, width: "8%", padding: "4px 2px"}} rowSpan="2">District</th>
                <th style={{...thStyle, width: "8%", padding: "4px 2px"}} rowSpan="2">ULB</th>
                <th style={{...thStyle, width: "10%", padding: "4px 2px"}} rowSpan="2">Employee</th>
                <th style={{...thStyle, width: "8%", padding: "4px 2px"}} rowSpan="2">Phone</th>
                <th style={{...thStyle, width: "8%", padding: "4px 2px"}} rowSpan="2">Designation</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:theme.colors.primaryDark}} rowSpan="2">WD</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#2e7d32"}} rowSpan="2">P</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#c62828"}} rowSpan="2">A</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#ed6c02"}} rowSpan="2">H</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px"}} rowSpan="2">S</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#0288d1"}} rowSpan="2">Lv</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#f59e0b"}} rowSpan="2">Late</th>
                <th style={{...thStyle, width: "3%", padding: "4px 2px", backgroundColor:"#9c27b0"}} rowSpan="2">Early</th>
                <th style={{...thStyle, width: "4%", padding: "4px 2px", backgroundColor:"#d32f2f"}} rowSpan="2">CL Ded</th>
                {leaveTypes.sort().map(leave => (
                  <th key={leave} style={{...thStyle, width: "9%", backgroundColor:"#7b1fa2", padding: "4px 2px"}} colSpan="3">{formatLeaveName(leave)}</th>
                ))}
              </tr>
              <tr>
                {leaveTypes.sort().map(leave => (
                  <React.Fragment key={leave}>
                    <th style={{...thStyle, fontSize:"0.6rem", backgroundColor:"#9575cd", padding: "2px"}}>Elg</th>
                    <th style={{...thStyle, fontSize:"0.6rem", backgroundColor:"#9575cd", padding: "2px"}}>Avl</th>
                    <th style={{...thStyle, fontSize:"0.6rem", backgroundColor:"#9575cd", padding: "2px"}}>Rmn</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr><td colSpan={totalColumns} style={{ textAlign:"center", padding:"40px", color:"red" }}>{error}</td></tr>
              ) : attendanceReport.length === 0 ? (
                <tr><td colSpan={totalColumns} style={{ textAlign:"center", padding:"40px" }}>No records found</td></tr>
              ) : (
                attendanceReport.map((att, idx) => {
                  const s = att.attendance_summary || {};
                  return (
                    <tr key={att.user_id} style={{ borderBottom:"1px solid #eee", backgroundColor: idx%2===0 ? "#fff" : "#fafafa" }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor="#f0f7ff"}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor= idx%2===0 ? "#fff" : "#fafafa"}>
                      <td style={{...tdStyle, fontWeight:"bold", color:theme.colors.grayDark, padding: "2px"}}>{(reportPagination?.currentPage-1)*itemsPerPage + idx + 1}</td>
                      <td style={{...tdStyle, padding: "2px"}}>{att.district}</td>
                      <td style={{...tdStyle, padding: "2px"}}>{att.municipality}</td>
                      <td style={{...tdStyle, fontWeight:500, padding: "2px"}}>{att.employee_name}</td>
                      <td style={{...tdStyle, padding: "2px"}}>{att.phone}</td>
                      <td style={{...tdStyle, padding: "2px"}}>{att.designation}</td>
                      <td style={{...tdStyle, fontWeight:"bold", textAlign:"center", padding: "2px"}}>{s.working_days||0}</td>
                      <td style={{...tdStyle, color:"#2e7d32", fontWeight:"bold", textAlign:"center", padding: "2px"}}>{s.present||0}</td>
                      <td style={{...tdStyle, color:"#c62828", fontWeight:s.absent>0?"bold":"normal", textAlign:"center", padding: "2px"}}>{s.absent||0}</td>
                      <td style={{...tdStyle, color:"#ed6c02", textAlign:"center", padding: "2px"}}>{s.holiday||0}</td>
                      <td style={{...tdStyle, textAlign:"center", padding: "2px"}}>{s.sunday||0}</td>
                      <td style={{...tdStyle, color:"#0288d1", textAlign:"center", padding: "2px"}}>{s.approved_leaves||0}</td>
                      <td style={{...tdStyle, textAlign:"center", padding: "2px"}}>{formatCountBadge(s.late, 'late')}</td>
                      <td style={{...tdStyle, textAlign:"center", padding: "2px"}}>{formatCountBadge(s.early_departure, 'early')}</td>
                      <td style={{...tdStyle, textAlign:"center", color: s.cl_deduction>0 ? "#d32f2f" : "#10b981", fontWeight: s.cl_deduction>0 ? "bold" : "normal", padding: "2px"}}>{s.cl_deduction||0}</td>
                      {leaveTypes.sort().map(leave => {
                        const ls = att.leave_summary?.[leave] || {};
                        return (
                          <React.Fragment key={leave}>
                            <td style={{...tdStyle, textAlign:"center", padding: "2px"}}>{ls.eligible||0}</td>
                            <td style={{...tdStyle, color:"#0288d1", textAlign:"center", padding: "2px"}}>{ls.availed||0}</td>
                            <td style={{...tdStyle, fontWeight:"bold", textAlign:"center", padding: "2px"}}>{ls.remaining||0}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div style={{ padding: "4px 8px", backgroundColor: theme.colors.grayLight, borderTop: "1px solid #ddd", fontSize: "0.65rem", display: "flex", flexWrap: "wrap", gap: "10px", color: theme.colors.grayDark }}>
            <span><span style={{ color: "#2e7d32", fontWeight: "bold" }}>P</span> Present</span>
            <span><span style={{ color: "#c62828", fontWeight: "bold" }}>A</span> Absent</span>
            <span><span style={{ color: "#ed6c02", fontWeight: "bold" }}>H</span> Holiday</span>
            <span><span style={{ color: "#f59e0b", fontWeight: "bold" }}>Late</span> Late</span>
            <span><span style={{ color: "#9c27b0", fontWeight: "bold" }}>Early</span> Early</span>
            <span><span style={{ color: "#d32f2f", fontWeight: "bold" }}>CL Ded</span> CL Deduction</span>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: "center", gap: theme.spacing.sm, padding: theme.spacing.md, backgroundColor: "#fff", marginTop: theme.spacing.md, borderRadius: theme.radius.lg, boxShadow: theme.shadow.soft }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1 || loading || downloadingExcel}
          style={{ ...paginationButtonStyle, width: isMobile ? "100%" : "auto", opacity: (currentPage===1 || loading || downloadingExcel) ? 0.5 : 1 }}>
          ← Previous
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, fontSize: isMobile ? "0.8rem" : "0.9rem" }}>
          <span>Page <strong>{reportPagination?.currentPage||1}</strong> of <strong>{reportPagination?.totalPages||1}</strong></span>
          <span style={{ color: theme.colors.gray }}>•</span>
          <span>Total: <strong>{reportPagination?.totalRecords||0}</strong></span>
        </div>
        <button onClick={() => setCurrentPage(p => Math.min(reportPagination?.totalPages||1, p+1))} disabled={currentPage===reportPagination?.totalPages || loading || downloadingExcel}
          style={{ ...paginationButtonStyle, width: isMobile ? "100%" : "auto", opacity: (currentPage===reportPagination?.totalPages || loading || downloadingExcel) ? 0.5 : 1 }}>
          Next →
        </button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          width: 40px; height: 40px;
          border: 3px solid #e0e0e0;
          border-top-color: #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .spinner-small {
          width: 16px; height: 16px;
          border: 2px solid #ffffff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AttendanceReport;